/**
 * JdsV2 — Manage Jobs list page (UI-v2 Phase 1c).
 *
 * PORT of pages/app/Jds.tsx to the v2 shell.
 *
 * Layout (canonical design/v2-direction/index.html `#view-jobs`):
 *   Page head — "Jobs" eyebrow + title + "{N active · M total}" sub + [New job] CTA
 *   Toolbar   — search input · status filter chips · sort select
 *   DataTableV2 — Status · Job · Source / Upload · Candidates · Created · ⋮
 *   Empty / filter-empty states
 *
 * Shared patterns used:
 *   • DataTableV2   — table chrome, rows, hover, empty state
 *   • Button (shadcn ui) — CTA, ghost row actions
 *
 * Data: exact same hooks as v1 (useJds, useDeleteJd, useUpdateJdStatus).
 * Mutations: identical to v1 (handlePause, handleArchive, handleConfirmDelete).
 *
 * Token discipline: no hex / hsl(var()) / rgb literals in this file.
 * All colors via semantic Tailwind utilities or var(--token) references.
 *
 * Density rules: v2 density ceiling enforced — no oversize spacing tokens.
 */
import { useEffect, useMemo, useState, type JSX } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useJds, type JdStatusFilter } from "@/hooks/queries/candidates/useJds";
import { useDeleteJd } from "@/hooks/queries/candidates/useDeleteJd";
import { useUpdateJdStatus } from "@/hooks/queries/candidates/useUpdateJdStatus";
import { JdDeleteConfirmModal } from "@/components/jds/JdDeleteConfirmModal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTableV2, type DataTableColumn } from "@/components/v2/patterns/DataTableV2";
import { useToastStore } from "@/stores/useToastStore";
import type { Jd } from "@/hooks/queries/candidates/useJd";
import { cn } from "@/lib/utils";

type SortOrder = "newest" | "oldest" | "az" | "za";

const FILTER_OPTIONS: { value: JdStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
  { value: "paused", label: "Paused" },
];

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: Jd["status"] }): JSX.Element {
  const map: Record<Jd["status"], { label: string; className: string }> = {
    active: { label: "Active", className: "bg-[hsl(var(--strong-soft))] text-[hsl(var(--strong))]" },
    draft: { label: "Draft", className: "bg-surface-subtle text-text-primary" },
    paused: { label: "Paused", className: "bg-[hsl(var(--moderate-soft))] text-[hsl(var(--moderate))]" },
    archived: { label: "Archived", className: "bg-surface-subtle text-text-tertiary" },
  };
  const { label, className } = map[status] ?? map.draft;
  return (
    <span
      className={cn(
        "inline-flex h-[21px] items-center rounded-full border border-transparent",
        "text-[11.5px] font-semibold leading-none",
        "px-2",
        className,
      )}
    >
      {label}
    </span>
  );
}

// ── Table columns definition ─────────────────────────────────────────────────

const COLS: DataTableColumn[] = [
  { key: "status", header: "Status", width: "100px" },
  { key: "name",   header: "Job" },
  { key: "source", header: "Source", width: "190px" },
  { key: "candidates", header: "Candidates", width: "110px", headClassName: "text-right", cellClassName: "text-right tabular-nums" },
  { key: "created", header: "Created", width: "130px", cellClassName: "tabular-nums text-text-secondary" },
  { key: "actions", header: "", width: "36px" },
];

// ── Row kebab menu ────────────────────────────────────────────────────────────

function RowMenu({
  jd,
  onEdit,
  onReactivate,
  onPause,
  onArchive,
  onDelete,
}: {
  jd: Jd;
  onEdit: () => void;
  onReactivate: () => void;
  onPause: () => void;
  onArchive: () => void;
  onDelete: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  // Close on Escape (keyboard affordance parity with the JdDetail kebab).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const items: { label: string; action: () => void; destructive?: boolean }[] = [
    { label: "Edit", action: onEdit },
  ];
  if (jd.status !== "active") items.push({ label: "Reactivate", action: onReactivate });
  if (jd.status === "active") items.push({ label: "Pause", action: onPause });
  if (jd.status !== "archived") items.push({ label: "Archive", action: onArchive });
  items.push({ label: "Delete", action: onDelete, destructive: true });

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="More actions"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={cn(
          "grid h-[28px] w-[28px] place-items-center rounded-[var(--radius,0.5rem)]",
          "border border-transparent text-text-tertiary",
          "transition-colors hover:bg-surface-card-hover hover:text-text-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
        )}
      >
        <DotsIcon />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className={cn(
              "absolute right-0 top-full z-20 mt-1 w-36 overflow-hidden",
              "rounded-[var(--radius,0.5rem)] border border-border-default",
              "bg-surface-elevated py-1 shadow-[var(--shadow-pop)]",
            )}
          >
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); it.action(); }}
                className={cn(
                  "block w-full px-3 py-2 text-left text-[13px]",
                  "transition-colors hover:bg-surface-subtle",
                  "focus:outline-none",
                  it.destructive ? "text-verdict-gap-fg" : "text-text-primary",
                )}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Cell renderer ────────────────────────────────────────────────────────────

function renderCell(
  jd: Jd,
  col: DataTableColumn,
  tenantSlug: string | undefined,
  onEdit: (jd: Jd) => void,
  onReactivate: (jd: Jd) => void,
  onPause: (jd: Jd) => void,
  onArchive: (jd: Jd) => void,
  onRequestDelete: (jd: Jd) => void,
): JSX.Element {
  switch (col.key) {
    case "status":
      return <StatusBadge status={jd.status} />;
    case "name":
      return (
        <div className="min-w-0">
          <div className="truncate font-[550] text-text-primary" style={{ fontSize: "13.5px" }}>
            {jd.name}
          </div>
          {jd.parsed_fields?.location ? (
            <div className="truncate text-text-tertiary" style={{ fontSize: "12px" }}>
              {jd.parsed_fields.location}
            </div>
          ) : null}
        </div>
      );
    case "source": {
      // v1 JdListTable: uploaded file → download link; pasted text → italic label.
      const isUpload = Boolean(jd.filename);
      if (isUpload && tenantSlug) {
        return (
          <a
            href={`/api/t/${tenantSlug}/jds/${jd.id}/document`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "inline-flex max-w-full items-center gap-1.5 truncate text-left",
              "text-[13px] text-[hsl(var(--brand))] transition-colors",
              "hover:underline focus:outline-none focus-visible:underline",
            )}
          >
            <DownloadInlineIcon />
            <span className="truncate">{jd.filename}</span>
          </a>
        );
      }
      return <span className="italic text-text-secondary" style={{ fontSize: "12.5px" }}>Pasted text</span>;
    }
    case "candidates":
      // candidate_count is returned by the JD LIST endpoint; null → "—".
      return <span>{jd.candidate_count ?? "—"}</span>;
    case "created":
      return (
        <span>
          {new Date(jd.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      );
    case "actions":
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <RowMenu
            jd={jd}
            onEdit={() => onEdit(jd)}
            onReactivate={() => onReactivate(jd)}
            onPause={() => onPause(jd)}
            onArchive={() => onArchive(jd)}
            onDelete={() => onRequestDelete(jd)}
          />
        </div>
      );
    default:
      return <></>;
  }
}

// ── Page head ────────────────────────────────────────────────────────────────

function PageHead({
  activeCount,
  draftCount,
  totalCount,
  tenantSlug,
  loading,
}: {
  activeCount: number;
  draftCount: number;
  totalCount: number;
  tenantSlug: string | undefined;
  loading: boolean;
}): JSX.Element {
  const base = useTenantBase();
  // Mockup-faithful summary (design/v2-direction #view-jobs → "4 roles · 3 open · 1 draft").
  // Real counts; "active" mirrors the status chips/badges (the mockup's "open" is
  // illustrative). The "draft" segment only appears when there are drafts.
  const summary = [
    `${totalCount} ${totalCount === 1 ? "role" : "roles"}`,
    `${activeCount} active`,
    ...(draftCount > 0 ? [`${draftCount} draft`] : []),
  ].join(" · ");
  return (
    <div className="flex items-center gap-[14px]" style={{ marginBottom: "22px" }}>
      <div className="min-w-0 flex-1">
        <div
          className="font-bold uppercase text-[hsl(var(--brand,214_100%_41%))]"
          style={{ fontSize: "11px", letterSpacing: "0.08em" }}
        >
          Roles
        </div>
        <h1
          className="text-text-primary"
          style={{ fontSize: "22px", fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}
        >
          Manage Jobs
        </h1>
        {!loading && (
          <div className="text-text-secondary" style={{ fontSize: "13.5px", marginTop: "3px" }}>
            {summary}
          </div>
        )}
      </div>
      {tenantSlug ? (
        <Link to={`${base}/jobs/new`}>
          <Button size="sm">
            <PlusIcon />
            New job
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

// ── Toolbar ──────────────────────────────────────────────────────────────────

function Toolbar({
  searchTerm,
  onSearch,
  statusFilter,
  onStatusFilter,
  showArchived,
  onToggleArchived,
  sortOrder,
  onSort,
}: {
  searchTerm: string;
  onSearch: (v: string) => void;
  statusFilter: JdStatusFilter;
  onStatusFilter: (v: JdStatusFilter) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
  sortOrder: SortOrder;
  onSort: (v: SortOrder) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3" style={{ marginBottom: "16px" }}>
      {/* Search */}
      <div className="relative w-full max-w-[260px]">
        <span className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-text-tertiary">
          <SearchIcon />
        </span>
        <input
          type="search"
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search jobs…"
          aria-label="Search jobs"
          className={cn(
            "h-[36px] w-full rounded-[var(--radius,0.5rem)]",
            "border border-border-strong bg-surface-canvas",
            "pl-[34px] pr-3 text-[13.5px] text-text-primary",
            "placeholder:text-text-tertiary",
            "focus:outline-none focus:border-border-focus focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--border-focus)_16%,transparent)]",
          )}
        />
      </div>

      {/* Right controls */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status filter chips (shadcn-style seg) */}
        <div
          role="tablist"
          aria-label="Filter by status"
          className="inline-flex gap-[2px] rounded-[var(--radius,0.5rem)] bg-surface-subtle p-[3px]"
        >
          {FILTER_OPTIONS.map((opt) => {
            const active = !showArchived && statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => { onStatusFilter(opt.value); }}
                className={cn(
                  "h-[28px] rounded-[calc(var(--radius,0.5rem)-3px)] px-[11px]",
                  "text-[12.5px] font-[550] transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
                  active
                    ? "bg-surface-canvas text-text-primary shadow-[var(--shadow-sm)]"
                    : "text-text-secondary hover:text-text-primary",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onToggleArchived}
          aria-pressed={showArchived}
          className={cn(
            "h-[28px] rounded-[var(--radius,0.5rem)] px-[11px]",
            "text-[12.5px] font-[550] transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
            showArchived
              ? "bg-surface-subtle text-text-primary"
              : "text-text-tertiary hover:text-text-primary",
          )}
        >
          {showArchived ? "Hide archived" : "Archived"}
        </button>

        {/* Sort */}
        <label className="inline-flex items-center gap-2 text-[12px] text-text-tertiary">
          <span className="sr-only">Sort by</span>
          <Select value={sortOrder} onValueChange={(v) => onSort(v as SortOrder)}>
            <SelectTrigger className="h-[30px] w-[130px] text-[12.5px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="az">A–Z</SelectItem>
              <SelectItem value="za">Z–A</SelectItem>
            </SelectContent>
          </Select>
        </label>
      </div>
    </div>
  );
}

// ── Empty states ─────────────────────────────────────────────────────────────

function EmptyState({ tenantSlug }: { tenantSlug: string | undefined }): JSX.Element {
  const base = useTenantBase();
  return (
    <div className="mx-auto mt-4 flex w-full max-w-md flex-col items-center text-center">
      <BriefcaseIcon />
      <h2 className="mt-4 text-[18px] font-semibold text-text-primary">No jobs yet</h2>
      <p className="mt-2 text-[13.5px] text-text-secondary">
        Create your first job to start ranking candidates.
      </p>
      {tenantSlug ? (
        <Link to={`${base}/jobs/new`} className="mt-5">
          <Button>Create your first job</Button>
        </Link>
      ) : null}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function JdsV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<JdStatusFilter>("all");
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const addToast = useToastStore((s) => s.addToast);

  const effectiveFilter: JdStatusFilter = showArchived ? "archived" : statusFilter;
  const jdsQ = useJds({ statusFilter: effectiveFilter });
  const del = useDeleteJd();
  const updateStatus = useUpdateJdStatus();

  const visibleJds = useMemo(() => {
    const all = jdsQ.data ?? [];
    const term = searchTerm.trim().toLowerCase();
    const filtered = term
      ? all.filter((j) => {
          if (j.name.toLowerCase().includes(term)) return true;
          return (j.parsed_fields?.location ?? "").toLowerCase().includes(term);
        })
      : all;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (sortOrder) {
        case "newest": return +new Date(b.created_at) - +new Date(a.created_at);
        case "oldest": return +new Date(a.created_at) - +new Date(b.created_at);
        case "az":     return a.name.localeCompare(b.name);
        case "za":     return b.name.localeCompare(a.name);
      }
    });
    return sorted;
  }, [jdsQ.data, searchTerm, sortOrder]);

  const allJds = jdsQ.data ?? [];
  const activeCount = allJds.filter((j) => j.status === "active").length;
  const draftCount = allJds.filter((j) => j.status === "draft").length;
  const totalCount = allJds.length;

  const isEmpty = jdsQ.isSuccess && allJds.length === 0 && searchTerm.trim() === "";
  const isFilterEmpty = jdsQ.isSuccess && allJds.length > 0 && visibleJds.length === 0;

  const handleConfirmDelete = (): void => {
    if (!pendingDelete) return;
    del.mutate(pendingDelete.id, {
      onSuccess: () => { addToast(`Deleted job: ${pendingDelete.name}.`, "success"); setPendingDelete(null); },
      onError:   (err) => { addToast(err.message || "Couldn't delete. Try again.", "error"); setPendingDelete(null); },
    });
  };

  const handlePause = (jd: Jd): void => {
    updateStatus.mutate(
      { id: jd.id, status: "paused" },
      {
        onSuccess: () => addToast(`Paused: ${jd.name}.`, "success"),
        onError:   (err) => addToast(err.message || "Couldn't pause this job.", "error"),
      },
    );
  };

  const handleArchive = (jd: Jd): void => {
    updateStatus.mutate(
      { id: jd.id, status: "archived" },
      {
        onSuccess: () => addToast(`Archived: ${jd.name}.`, "success"),
        onError:   (err) => addToast(err.message || "Couldn't archive this job.", "error"),
      },
    );
  };

  const handleReactivate = (jd: Jd): void => {
    updateStatus.mutate(
      { id: jd.id, status: "active" },
      {
        onSuccess: () => addToast(`Reactivated: ${jd.name}.`, "success"),
        onError:   (err) => addToast(err.message || "Couldn't reactivate this job.", "error"),
      },
    );
  };

  return (
    <div data-testid="page-ready">
      <PageHead
        activeCount={activeCount}
        draftCount={draftCount}
        totalCount={totalCount}
        tenantSlug={tenantSlug}
        loading={jdsQ.isLoading}
      />

      {jdsQ.isLoading && (
        <p className="text-[13.5px] text-text-secondary">Loading…</p>
      )}
      {jdsQ.isError && (
        <p className="text-[13.5px] text-verdict-gap-fg">
          {jdsQ.error?.message ?? "Couldn't load jobs. Refresh."}
        </p>
      )}

      {jdsQ.isSuccess && isEmpty && <EmptyState tenantSlug={tenantSlug} />}

      {jdsQ.isSuccess && !isEmpty && (
        <>
          <Toolbar
            searchTerm={searchTerm}
            onSearch={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilter={(v) => { setStatusFilter(v); setShowArchived(false); }}
            showArchived={showArchived}
            onToggleArchived={() => setShowArchived((v) => !v)}
            sortOrder={sortOrder}
            onSort={setSortOrder}
          />

          {isFilterEmpty ? (
            <div
              className={cn(
                "rounded-[var(--radius,0.5rem)] border border-border-default",
                "bg-surface-card p-4 text-center",
              )}
              data-testid="jds-filter-empty"
            >
              <p className="text-[13.5px] text-text-secondary">No jobs match your filters.</p>
              <button
                type="button"
                onClick={() => { setSearchTerm(""); setStatusFilter("all"); setShowArchived(false); }}
                className="mt-2 text-[13px] text-[hsl(var(--brand))] hover:text-[hsl(var(--brand))] hover:underline focus:outline-none"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <DataTableV2
              columns={COLS}
              rows={visibleJds}
              getRowKey={(jd) => jd.id}
              onRowClick={(jd) => navigate(`${base}/jobs/${jd.id}`)}
              renderCell={(jd, col) =>
                renderCell(
                  jd,
                  col,
                  tenantSlug,
                  (j) => navigate(`${base}/jobs/${j.id}/edit`),
                  handleReactivate,
                  handlePause,
                  handleArchive,
                  (j) => setPendingDelete({ id: j.id, name: j.name }),
                )
              }
              emptyState="No jobs match your filters."
            />
          )}
        </>
      )}

      {pendingDelete && (
        <JdDeleteConfirmModal
          jdName={pendingDelete.name}
          onCancel={() => setPendingDelete(null)}
          onConfirm={handleConfirmDelete}
          pending={del.isPending}
        />
      )}
    </div>
  );
}

// ── SVG icons (no raw color) ─────────────────────────────────────────────────

function PlusIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      width={15} height={15} aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function SearchIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      width={15} height={15} aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function DotsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" width={15} height={15} aria-hidden>
      <circle cx="8" cy="3" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="13" r="1.4" />
    </svg>
  );
}

function DownloadInlineIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round" width={12} height={12}
      aria-hidden className="shrink-0">
      <path d="M6 2v6" />
      <path d="M3 5l3 3 3-3" />
      <path d="M2 10h8" />
    </svg>
  );
}

function BriefcaseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round" width={48} height={48}
      aria-hidden className="text-text-tertiary">
      <rect x="6" y="14" width="36" height="26" rx="3" />
      <path d="M18 14V10a3 3 0 013-3h6a3 3 0 013 3v4" />
      <path d="M6 24h36" />
    </svg>
  );
}
